import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  StarIcon,
  TicketIcon,
  WifiIcon,
} from '@heroicons/react/24/outline'
import type { AgentDescriptor } from 'tailwind-v4/blocks/agent-chat'
import type { QuickAction } from 'tailwind-v4/blocks/chat'

/**
 * Single source of truth for the Knowledge Base agent's descriptor.
 * Previously duplicated between chat-3/page.tsx and this destination's own
 * page.tsx, which drifted (different greeting/prompts) within the same
 * session — consolidated here and imported by every surface that talks to
 * this agent (the full page, the side panel, and chat-3's picker entry).
 */
export const KNOWLEDGE_BASE_AGENT: AgentDescriptor = {
  key: 'knowledge-base',
  label: 'Knowledge Base',
  greeting:
    "Hi — I'm the Knowledge Base assistant. Ask me how to configure or find anything in the product and I'll link you straight to it.",
  // Flat fallback — used by the narrow docked side panel (agent-chat's
  // `ChatPanel`/`transcript.tsx`), which has no room for the tabbed
  // `KNOWLEDGE_BASE_QUICK_ACTIONS` groups below. Keep this list the same
  // "Getting started" five so the first thing a new user sees is
  // consistent whether they're in the dock or the full page.
  suggestedPrompts: [
    'How do I set up my email sending domains?',
    'How do I connect my Stripe account?',
    'How do I connect my Google reviews?',
    'What do you need from me to connect Square?',
    'How do I create a booking type?',
  ],
  available: true,
}

/**
 * Sectioned quick-action prompts for the full-size surfaces (`chat-3` and
 * the dedicated `/knowledge-base` page, both on `ChatShell`/`EmptyThread`,
 * which has room for a tab strip). "Getting started" is listed first so
 * it's the tab shown by default, is what a new user sees on opening a
 * fresh chat, mirrors the account-setup checklist sent to every new
 * customer. The rest are pulled from the recurring support-ticket themes
 * mined from staff mailboxes (see kb-mining), one section per Knowledge
 * Base content category, weighted towards the highest-evidence themes.
 *
 * Icons MUST be wrapped in a plain arrow function, not passed as the
 * Heroicon component reference directly — Heroicons v2 icons are
 * `React.forwardRef` objects (`{$$typeof, render}`), not functions, so
 * `EmptyThread`'s `renderIcon` (`typeof icon === 'function'`) fails its
 * function check for them and returns the raw object as a child, which
 * React then refuses to render. This is why this file is `.tsx`.
 */
export const KNOWLEDGE_BASE_QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'getting-started',
    label: 'Getting started',
    icon: (props: { className?: string }) => <RocketLaunchIcon {...props} />,
    prompts: [
      'How do I set up my email sending domains?',
      'How do I connect my Stripe account?',
      'How do I connect my Google reviews?',
      'What do you need from me to connect Square?',
      'How do I create a booking type?',
    ],
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: (props: { className?: string }) => <CalendarDaysIcon {...props} />,
    prompts: [
      "Why isn't my booking confirmation email arriving?",
      "Why is online booking showing no availability?",
      'Where do I find my bookings report?',
    ],
  },
  {
    id: 'wifi',
    label: 'Wi-Fi',
    icon: (props: { className?: string }) => <WifiIcon {...props} />,
    prompts: [
      'My guest Wi-Fi looks offline, what should I check?',
      'How do I change my guest Wi-Fi network name?',
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: (props: { className?: string }) => <MegaphoneIcon {...props} />,
    prompts: [
      'Why did my scheduled SMS campaign not send?',
      "How do I email a specific event's guest list?",
    ],
  },
  {
    id: 'reviews',
    label: 'Reviews',
    icon: (props: { className?: string }) => <StarIcon {...props} />,
    prompts: [
      "Why aren't reviews coming through for a venue?",
      'How do I change the background image on my reviews page?',
    ],
  },
  {
    id: 'loyalty',
    label: 'Loyalty',
    icon: (props: { className?: string }) => <TicketIcon {...props} />,
    prompts: [
      'How do I award loyalty points by spend rather than per visit?',
      "How long does a loyalty wallet-pass update take to reach a guest's phone?",
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: (props: { className?: string }) => <PuzzlePieceIcon {...props} />,
    prompts: [
      'How do I connect Google Reserve?',
      'How do I update an expired card on file?',
    ],
  },
  {
    id: 'org-account',
    label: 'Org & account',
    icon: (props: { className?: string }) => <BuildingOfficeIcon {...props} />,
    prompts: [
      'How do I add another venue to my account?',
      'Why am I being billed for a site with no active service?',
    ],
  },
]
